"use client";

import Link from "next/link";
import { SiteHero, FaqList, CtaBand } from "@/components/marketing/site-ui";
import { buttonClasses } from "@/lib/ui/buttonClasses";
import { useSiteContent } from "@/components/marketing/use-site-content";

/**
 * The FAQ.
 *
 * Grouped rather than one long list, because the questions divide cleanly into four things
 * people are actually deciding: what is it, will anyone talk to me, can I run something on
 * it, and can I trust it with my data.
 *
 * Each group emits its own FAQPage schema through FaqList. That is what answer engines read
 * when somebody asks ChatGPT or an AI Overview what GrowthSync is, so these answers are
 * written to be quoted verbatim: one claim per sentence, no marketing adjectives.
 */
export function FaqClient() {
  const c = useSiteContent();
  const page = c.faqPage;

  return (
    <>
      <SiteHero
        eyebrow={page.eyebrow}
        title={page.title}
        lede={page.lede}
        primaryLabel={c.cta.primary}
        secondaryHref="/contact"
        secondaryLabel={c.company.contact}
      />

      <section className="border-t border-neutral-200 bg-white pb-16 pt-14 md:pb-24 md:pt-16">
        <div className="gs-page">
          <div className="grid gap-12 lg:grid-cols-[minmax(0,0.4fr)_minmax(0,1fr)] lg:gap-16">
            {/* A left rail of the four groups, so a long page still has a shape you can see
                from the top. It is a plain anchor list: no sticky scroll spy, which on a
                phone is a lot of JavaScript to reproduce a heading. */}
            <nav aria-label={page.eyebrow} className="min-w-0 lg:sticky lg:top-28 lg:self-start">
              <ul className="flex flex-wrap gap-2 lg:flex-col lg:gap-1">
                {page.groups.map((group) => (
                  <li key={group.title}>
                    <a
                      href={`#${group.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="inline-flex rounded-full border border-neutral-200 px-4 py-2 text-[13.5px] font-semibold text-neutral-600 transition-colors hover:border-primary/40 hover:text-primary lg:rounded-lg lg:border-0 lg:px-0 lg:py-1.5"
                    >
                      {group.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="min-w-0 space-y-14">
              {page.groups.map((group) => (
                <div key={group.title} id={group.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")} className="scroll-mt-28">
                  <FaqList heading={group.title} items={group.items} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="gs-page">
          <div className="flex flex-col items-start justify-between gap-6 rounded-[1.75rem] border border-neutral-200 bg-white p-8 md:flex-row md:items-center md:p-10">
            <div className="min-w-0">
              <h2 className="gs-title text-neutral-900">{page.stillHeading}</h2>
              <p className="mt-2 text-[15px] leading-relaxed text-neutral-600">{page.stillBody}</p>
            </div>
            <Link href="/contact" className={buttonClasses({ size: "md", className: "shrink-0" })}>
              {page.stillCta}
            </Link>
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
